import type { IRawTiledObject } from '@nova/types';

import { GeomUtils } from '../geom-utils';
import { lineSegmentsTouch } from '../line-utils';

import { type ITileGridStep, TILE_GRID_NEIGHBORS_4, TILE_GRID_NEIGHBORS_8 } from './tile-grid';

/**
 * How blocked-tile clusters become navmesh hole polygons.
 * - separatedTiles: one hole per tile (diagonal gap stays open).
 * - diagonalBlocked: merge corner-touching tiles and cut shared corners.
 * - bufferedJoints: merge and block diagonals, but keep open nooks nearly walkable.
 */
export const MAP_GEN_NAV_HOLE_MODES = ['separatedTiles', 'diagonalBlocked', 'bufferedJoints'] as const;
export type MapGenNavHoleMode = (typeof MAP_GEN_NAV_HOLE_MODES)[number];
interface IGridCorner {
  cx: number;
  cy: number;
}

interface IGridEdge {
  from: IGridCorner;
  to: IGridCorner;
}

interface IGridCell {
  col: number;
  row: number;
}

interface IGridComponent {
  cells: IGridCell[];
}

interface IWorldPoint {
  x: number;
  y: number;
}

interface IBlobTrace {
  loops: IGridCorner[][];
  sharedCorners: Set<string>;
}
/** Pixels each shared diagonal corner is nudged into its open nook in buffered-joint hole mode. */
const JOINT_BUFFER_PX = 18;

function cornerKey(corner: IGridCorner): string {
  return `${corner.cx},${corner.cy}`;
}

function edgeKey(from: IGridCorner, to: IGridCorner): string {
  return `${cornerKey(from)}->${cornerKey(to)}`;
}

function blockedAt(grid: Uint8Array, width: number, col: number, row: number): boolean {
  if (col < 0 || row < 0 || col >= width) { return false; }
  const height = grid.length / width;
  if (row >= height) { return false; }
  return grid[(row * width) + col] === 1;
}

function collectBoundaryEdges(
  component: Uint8Array,
  width: number,
  height: number
): IGridEdge[] {
  const edges: IGridEdge[] = [];

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      if (!blockedAt(component, width, col, row)) { continue; }

      if (!blockedAt(component, width, col, row - 1)) {
        edges.push({ from: { cx: col, cy: row }, to: { cx: col + 1, cy: row } });
      }

      if (!blockedAt(component, width, col + 1, row)) {
        edges.push({ from: { cx: col + 1, cy: row }, to: { cx: col + 1, cy: row + 1 } });
      }

      if (!blockedAt(component, width, col, row + 1)) {
        edges.push({ from: { cx: col + 1, cy: row + 1 }, to: { cx: col, cy: row + 1 } });
      }

      if (!blockedAt(component, width, col - 1, row)) {
        edges.push({ from: { cx: col, cy: row + 1 }, to: { cx: col, cy: row } });
      }
    }
  }

  return edges;
}

function simplifyLoop(corners: IGridCorner[]): IGridCorner[] {
  const loop = removeClosingDuplicate(corners);
  if (loop.length < 4) { return loop; }

  const simplified: IGridCorner[] = [];
  for (let i = 0; i < loop.length; i++) {
    const prev = loop[(i + loop.length - 1) % loop.length];
    const curr = loop[i];
    const next = loop[(i + 1) % loop.length];
    const dx1 = curr.cx - prev.cx;
    const dy1 = curr.cy - prev.cy;
    const dx2 = next.cx - curr.cx;
    const dy2 = next.cy - curr.cy;
    if (dx1 === dx2 && dy1 === dy2) { continue; }
    simplified.push(curr);
  }
  return simplified.length >= 3 ? simplified : loop;
}

function removeClosingDuplicate(corners: IGridCorner[]): IGridCorner[] {
  if (corners.length < 2) { return corners; }
  const first = corners[0];
  const last = corners[corners.length - 1];
  if (first.cx !== last.cx || first.cy !== last.cy) { return corners; }
  return corners.slice(0, -1);
}

function traceMergedOutlines(component: Uint8Array, width: number, height: number): IGridCorner[][] {
  const edges = collectBoundaryEdges(component, width, height);
  const outgoing = new Map<string, IGridCorner[]>();

  for (const edge of edges) {
    const key = cornerKey(edge.from);
    const existing = outgoing.get(key);
    if (existing) {
      existing.push(edge.to);
    }
    else {
      outgoing.set(key, [edge.to]);
    }
  }

  const used = new Set<string>();
  const loops: IGridCorner[][] = [];

  for (const edge of edges) {
    const startKey = edgeKey(edge.from, edge.to);
    if (used.has(startKey)) { continue; }

    const loop: IGridCorner[] = [edge.from, edge.to];
    let prev = edge.from;
    let current = edge.to;
    used.add(startKey);

    while (true) {
      if (current.cx === edge.from.cx && current.cy === edge.from.cy && loop.length > 1) { break; }

      const next = pickNextBoundaryCorner(prev, current, outgoing.get(cornerKey(current)), used);
      if (!next) { break; }

      const nextKey = edgeKey(current, next);
      if (used.has(nextKey)) { break; }
      used.add(nextKey);
      loop.push(next);
      prev = current;
      current = next;

      if (loop.length > (width + height) * 4) { break; }
    }

    const simplified = simplifyLoop(loop);
    if (simplified.length >= 3) {
      loops.push(simplified);
    }
  }

  return loops;
}

function pickNextBoundaryCorner(
  prev: IGridCorner,
  current: IGridCorner,
  candidates: IGridCorner[] | undefined,
  used: Set<string>
): IGridCorner | undefined {
  if (!candidates) { return undefined; }
  let best: IGridCorner | undefined;
  let bestRank = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    if (used.has(edgeKey(current, candidate))) { continue; }
    const rank = boundaryTurnRank(prev, current, candidate);
    if (rank < bestRank) {
      best = candidate;
      bestRank = rank;
    }
  }

  return best;
}

function boundaryTurnRank(prev: IGridCorner, current: IGridCorner, next: IGridCorner): number {
  const incoming = directionIndex(current.cx - prev.cx, current.cy - prev.cy);
  const outgoing = directionIndex(next.cx - current.cx, next.cy - current.cy);
  if (incoming === undefined || outgoing === undefined) { return 4; }

  const turn = (outgoing - incoming + 4) % 4;
  if (turn === 1) { return 0; }

  if (turn === 0) { return 1; }

  if (turn === 3) { return 2; }
  return 3;
}

function directionIndex(dx: number, dy: number): number | undefined {
  if (dx > 0 && dy === 0) { return 0; }

  if (dx === 0 && dy > 0) { return 1; }

  if (dx < 0 && dy === 0) { return 2; }

  if (dx === 0 && dy < 0) { return 3; }
  return undefined;
}

function findGridComponents(
  grid: Uint8Array,
  width: number,
  height: number,
  neighbors: readonly ITileGridStep[]
): IGridComponent[] {
  const visited = new Uint8Array(grid.length);
  const components: IGridComponent[] = [];

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const startIndex = (row * width) + col;
      if (visited[startIndex] || !blockedAt(grid, width, col, row)) { continue; }

      const cells: IGridCell[] = [];
      const queue: IGridCell[] = [{ col, row }];
      visited[startIndex] = 1;

      for (let i = 0; i < queue.length; i++) {
        const current = queue[i];
        cells.push(current);

        for (const neighbor of neighbors) {
          const nextCol = current.col + neighbor.dc;
          const nextRow = current.row + neighbor.dr;
          if (!blockedAt(grid, width, nextCol, nextRow)) { continue; }

          const nextIndex = (nextRow * width) + nextCol;
          if (visited[nextIndex]) { continue; }
          visited[nextIndex] = 1;
          queue.push({ col: nextCol, row: nextRow });
        }
      }

      components.push({ cells });
    }
  }

  return components;
}

function componentToGrid(component: IGridComponent, width: number, height: number): Uint8Array {
  const grid = new Uint8Array(width * height);
  for (const cell of component.cells) {
    grid[(cell.row * width) + cell.col] = 1;
  }
  return grid;
}

function polygonToNavHole(corners: IGridCorner[], tilePx: number, insetPx: number, id: number): IRawTiledObject | undefined {
  const center = getCornerCenter(corners);
  const rawPolygon = corners
    .filter((corner, index, arr) => {
      if (index === 0) { return true; }
      const prev = arr[index - 1];
      return corner.cx !== prev.cx || corner.cy !== prev.cy;
    })
    .map((corner) => ({
      x: (corner.cx * tilePx) + Math.sign(center.x - corner.cx) * insetPx,
      y: (corner.cy * tilePx) + Math.sign(center.y - corner.cy) * insetPx
    }));

  return finalizeNavHole(rawPolygon, id);
}

/** Sanitizes a world-space polygon, rejects degenerate or self-touching shapes, and wraps it as a hole object. */
function finalizeNavHole(rawPolygon: IWorldPoint[], id: number): IRawTiledObject | undefined {
  const polygon = sanitizePolygonPoints(rawPolygon);

  if (polygon.length < 3 || Math.abs(GeomUtils.CalculateSignedPolygonArea(polygon)) < 1) {
    return undefined;
  }

  if (hasRepeatedPoint(polygon) || hasSelfIntersection(polygon)) {
    return undefined;
  }

  return {
    id,
    name: `wall-cluster-${id}`,
    type: '',
    class: '',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    rotation: 0,
    visible: true,
    polygon,
    properties: [{ name: 'navmeshHole', type: 'bool', value: true }]
  };
}

/**
 * Traces one outline per blob where tiles that touch only at a corner are joined into the same
 * polygon. At each shared corner the boundary would otherwise pinch (visit the same point twice).
 * The pinch point is dropped, which turns the two passes into straight diagonal edges. Collinear
 * diagonal runs then collapse, so a four-tile diagonal stair becomes a six-vertex skewed hexagon.
 *
 * When several diagonal contacts surround the same open tile, this can consume the whole tile-shaped
 * pocket. Use buffered joints when that pocket should stay mostly walkable.
 */
function traceBlobOutlinesWithCornerCuts(component: Uint8Array, width: number, height: number): IGridCorner[][] {
  const { loops, sharedCorners } = walkBlobLoops(component, width, height);
  const result: IGridCorner[][] = [];

  for (const loop of loops) {
    const cut = loop.filter((corner) => !sharedCorners.has(cornerKey(corner)));
    const simplified = simplifyLoop(cut);
    if (simplified.length >= 3) { result.push(simplified); }
  }

  return result;
}

/**
 * Walks the boundary of every blob where side-touching and corner-touching tiles belong to the same
 * blob. Each loop visits every shared corner twice; those corners are returned so callers can either
 * drop them (clean diagonal cut) or split them apart (buffered joint). Loops are raw: not simplified, with the
 * shared corners still present.
 */
function walkBlobLoops(component: Uint8Array, width: number, height: number): IBlobTrace {
  const edges = collectBoundaryEdges(component, width, height);
  const outgoing = new Map<string, IGridEdge[]>();

  for (const edge of edges) {
    const key = cornerKey(edge.from);
    const existing = outgoing.get(key);
    if (existing) { existing.push(edge); }
    else { outgoing.set(key, [edge]); }
  }

  const sharedCorners = new Set<string>();
  for (const [key, list] of outgoing) {
    if (list.length >= 2) { sharedCorners.add(key); }
  }

  const used = new Set<string>();
  const loops: IGridCorner[][] = [];

  for (const startEdge of edges) {
    if (used.has(edgeKey(startEdge.from, startEdge.to))) { continue; }

    const loop: IGridCorner[] = [];
    let current: IGridEdge | undefined = startEdge;

    while (current && !used.has(edgeKey(current.from, current.to))) {
      used.add(edgeKey(current.from, current.to));
      loop.push(current.from);
      current = pickCornerCutEdge(current, outgoing.get(cornerKey(current.to)), used);
      if (loop.length > (width + height) * 8) { break; }
    }

    loops.push(loop);
  }

  return { loops, sharedCorners };
}

/**
 * Buffered-joint variant. Keeps the full blob outline (corner-touching tiles stay one polygon) but
 * splits each shared corner into a point nudged a few pixels into the open diagonal nook. This keeps
 * the nook tiles almost fully walkable while still closing the diagonal gap, so players can step
 * close to the obstruction instead of losing a triangle to the hole as in the corner-cut hexagon.
 */
function traceBufferedJointPolygons(
  component: Uint8Array,
  width: number,
  height: number,
  tilePx: number,
  bufferPx: number
): IWorldPoint[][] {
  const { loops, sharedCorners } = walkBlobLoops(component, width, height);
  const polygons: IWorldPoint[][] = [];

  for (const loop of loops) {
    const simplified = simplifyLoop(loop);
    if (simplified.length < 3) { continue; }
    polygons.push(bufferedJointWorldPolygon(simplified, sharedCorners, tilePx, bufferPx));
  }

  return polygons;
}

function bufferedJointWorldPolygon(
  loop: IGridCorner[],
  sharedCorners: Set<string>,
  tilePx: number,
  bufferPx: number
): IWorldPoint[] {
  const points: IWorldPoint[] = [];

  for (let i = 0; i < loop.length; i++) {
    const curr = loop[i];
    if (!sharedCorners.has(cornerKey(curr))) {
      points.push({ x: curr.cx * tilePx, y: curr.cy * tilePx });
      continue;
    }

    const prev = loop[(i + loop.length - 1) % loop.length];
    const next = loop[(i + 1) % loop.length];
    const offsetX = Math.sign(next.cx - curr.cx) - Math.sign(curr.cx - prev.cx);
    const offsetY = Math.sign(next.cy - curr.cy) - Math.sign(curr.cy - prev.cy);
    const length = Math.hypot(offsetX, offsetY) || 1;

    points.push({
      x: (curr.cx * tilePx) + ((offsetX / length) * bufferPx),
      y: (curr.cy * tilePx) + ((offsetY / length) * bufferPx)
    });
  }

  return points;
}

/**
 * At a normal corner there is one outgoing edge. At a shared corner there are two, and the walker
 * crosses into the next tile so both corner-touching tiles share one outline.
 * With clockwise-emitted edges the crossing edge is the one whose turn has a cross product of -1.
 */
function pickCornerCutEdge(
  incoming: IGridEdge,
  candidates: IGridEdge[] | undefined,
  used: Set<string>
): IGridEdge | undefined {
  if (!candidates) { return undefined; }
  const available = candidates.filter((edge) => !used.has(edgeKey(edge.from, edge.to)));
  if (available.length === 0) { return undefined; }

  if (available.length === 1) { return available[0]; }

  const inX = incoming.to.cx - incoming.from.cx;
  const inY = incoming.to.cy - incoming.from.cy;
  for (const edge of available) {
    const outX = edge.to.cx - edge.from.cx;
    const outY = edge.to.cy - edge.from.cy;
    if (((inX * outY) - (inY * outX)) === -1) { return edge; }
  }

  return available[0];
}

function sanitizePolygonPoints(points: IWorldPoint[]): IWorldPoint[] {
  let sanitized = removeDuplicateNeighborPoints(points);
  sanitized = removeClosingDuplicatePoint(sanitized);

  let changed = true;
  while (changed && sanitized.length >= 3) {
    changed = false;
    const next: IWorldPoint[] = [];
    for (let i = 0; i < sanitized.length; i++) {
      const prev = sanitized[(i + sanitized.length - 1) % sanitized.length];
      const curr = sanitized[i];
      const after = sanitized[(i + 1) % sanitized.length];
      if (pointsEqual(prev, curr) || pointsEqual(curr, after) || areCollinear(prev, curr, after)) {
        changed = true;
        continue;
      }
      next.push(curr);
    }
    sanitized = removeClosingDuplicatePoint(removeDuplicateNeighborPoints(next));
  }

  return sanitized;
}

function removeDuplicateNeighborPoints(points: IWorldPoint[]): IWorldPoint[] {
  const next: IWorldPoint[] = [];
  for (const point of points) {
    const prev = next[next.length - 1];
    if (prev && pointsEqual(prev, point)) { continue; }
    next.push(point);
  }
  return next;
}

function removeClosingDuplicatePoint(points: IWorldPoint[]): IWorldPoint[] {
  if (points.length < 2) { return points; }
  const first = points[0];
  const last = points[points.length - 1];
  if (!pointsEqual(first, last)) { return points; }
  return points.slice(0, -1);
}

function pointsEqual(a: IWorldPoint, b: IWorldPoint): boolean {
  return a.x === b.x && a.y === b.y;
}

function hasRepeatedPoint(points: IWorldPoint[]): boolean {
  const seen = new Set<string>();
  for (const point of points) {
    const key = `${point.x},${point.y}`;
    if (seen.has(key)) { return true; }
    seen.add(key);
  }
  return false;
}

function hasSelfIntersection(points: IWorldPoint[]): boolean {
  for (let i = 0; i < points.length; i++) {
    const a1 = points[i];
    const a2 = points[(i + 1) % points.length];

    for (let j = i + 1; j < points.length; j++) {
      if (segmentsShareEndpoint(i, j, points.length)) { continue; }

      const b1 = points[j];
      const b2 = points[(j + 1) % points.length];
      if (lineSegmentsTouch(a1, a2, b1, b2)) { return true; }
    }
  }
  return false;
}

function segmentsShareEndpoint(aIndex: number, bIndex: number, pointCount: number): boolean {
  return aIndex === bIndex
    || (aIndex + 1) % pointCount === bIndex
    || aIndex === (bIndex + 1) % pointCount;
}

function areCollinear(a: IWorldPoint, b: IWorldPoint, c: IWorldPoint): boolean {
  return ((b.x - a.x) * (c.y - a.y)) === ((b.y - a.y) * (c.x - a.x));
}

function getCornerCenter(corners: IGridCorner[]): { x: number; y: number } {
  const total = corners.reduce(
    (acc, corner) => ({ x: acc.x + corner.cx, y: acc.y + corner.cy }),
    { x: 0, y: 0 }
  );
  return {
    x: total.x / Math.max(1, corners.length),
    y: total.y / Math.max(1, corners.length)
  };
}

export function gridToNavHoleObjects(
  grid: Uint8Array,
  width: number,
  height: number,
  tilePx: number,
  holeMode: MapGenNavHoleMode,
  firstObjectId: number
): IRawTiledObject[] {
  const holes: IRawTiledObject[] = [];
  let nextId = firstObjectId;
  const joinsDiagonals = holeMode === 'diagonalBlocked' || holeMode === 'bufferedJoints';
  const components = findGridComponents(grid, width, height, joinsDiagonals ? TILE_GRID_NEIGHBORS_8 : TILE_GRID_NEIGHBORS_4);

  for (const component of components) {
    const componentGrid = componentToGrid(component, width, height);

    if (holeMode === 'bufferedJoints') {
      const polygons = traceBufferedJointPolygons(componentGrid, width, height, tilePx, JOINT_BUFFER_PX);
      for (const polygon of polygons) {
        const hole = finalizeNavHole(polygon, nextId);
        if (!hole) { continue; }
        holes.push(hole);
        nextId++;
      }
      continue;
    }

    const loops = holeMode === 'diagonalBlocked'
      ? traceBlobOutlinesWithCornerCuts(componentGrid, width, height)
      : traceMergedOutlines(componentGrid, width, height);
    for (const loop of loops) {
      const hole = polygonToNavHole(loop, tilePx, holeMode === 'diagonalBlocked' ? 0 : 2, nextId);
      if (!hole) { continue; }
      holes.push(hole);
      nextId++;
    }
  }
  return holes;
}
